/**
 * Canvas2D drawing of one lights frame (plan sections 4 and 12). At night glow is a pre-rendered radial sprite per colour drawn
 * with additive ("lighter") compositing; by day nodes are soft fills with a crisp outline and a faint halo. Faint (not
 * traversed) edges are batched into one path per kind. Everything here reads the frame and draws; nothing is stored but sprites.
 *
 * frame = {view, cam, fitScale, layout, lights, display: Map<nid, current lit>, time, reducedMotion, hoverId, hoverEdgeId,
 *          selectedId, selectedEdgeId, theme (lightsTheme.readTheme), options (DEFAULT_OPTIONS shape)}
 */
import { toScreen } from "./lightsCamera";
import {
  EDGE, EDGE_KINDS, EDGE_MODES, HOVER_RADIUS_PX, LABELS, LEADING_HALO_SCALE, PULSE_DEPTH, PULSE_PERIOD_MS, ROLE_STYLE, ROLES,
  SELECTION, SHRUNK_RADIUS_SCALE, SPRITE, TONE_FIXED_ALPHA, TONES, ZOOM_RADIUS_MAX,
} from "./lightsConstants";
import { isNodeShown } from "./lightsState";
import { WHITE, mixRgb, rgbCss, toneRgb } from "./lightsTheme";

const CLUSTER_GLOW_BASE = 0.05;
const CLUSTER_GLOW_MASS = 0.3;
const CLUSTER_LABEL_ALPHA = 0.85;
const CLUSTER_PILL_ALPHA = 0.65;
const HALO_ALPHA = 0.5;
const CORE_WHITE_MIX = 0.55;
const CORE_RADIUS_SCALE = 0.45;
const DAY_FILL_RADIUS_SCALE = 0.75;
const DAY_HALO_MIN_LIT = 0.2;
const LABEL_BG_ALPHA = 0.7;
const LABEL_BG_RADIUS_PX = 4;
const MIN_ALPHA = 0.01;
const HOVER_RING_ALPHA = 0.8;

const sprites = new Map();

/** A radial glow sprite for one colour; `hot` puts a white-hot centre in it (night), else the colour fades out alone (day). */
function spriteFor(rgb, hot) {
  const key = `${rgb.join(",")}|${hot ? 1 : 0}`;
  if (sprites.has(key)) return sprites.get(key);
  if (typeof document === "undefined") return null;
  const c = document.createElement("canvas");
  c.width = SPRITE.SIZE;
  c.height = SPRITE.SIZE;
  const g = c.getContext("2d");
  if (!g) return null;
  const half = SPRITE.SIZE / 2;
  const grad = g.createRadialGradient(half, half, 0, half, half, half);
  const col = rgb.join(",");
  grad.addColorStop(0, hot ? "rgba(255,255,255,1)" : `rgba(${col},0.9)`);
  grad.addColorStop(0.12, `rgba(${col},${hot ? 0.95 : 0.6})`);
  grad.addColorStop(0.35, `rgba(${col},${hot ? 0.35 : 0.18})`);
  grad.addColorStop(1, `rgba(${col},0)`);
  g.fillStyle = grad;
  g.fillRect(0, 0, SPRITE.SIZE, SPRITE.SIZE);
  sprites.set(key, c);
  return c;
}

/** A filled rounded rectangle (plain rectangle where roundRect is unsupported). */
function pill(ctx, x, y, w, h, r) {
  ctx.beginPath();
  if (typeof ctx.roundRect === "function") ctx.roundRect(x, y, w, h, r);
  else ctx.rect(x, y, w, h);
  ctx.fill();
}

const zoomGain = (cam, fitScale) => Math.min(ZOOM_RADIUS_MAX, Math.max(1, Math.sqrt(cam.scale / fitScale)));
const pulseOf = (time, reducedMotion) => (reducedMotion ? 0 : PULSE_DEPTH * (0.5 + 0.5 * Math.sin((2 * Math.PI * time) / PULSE_PERIOD_MS)));
const blend = (ctx, f) => { ctx.globalCompositeOperation = f.theme.additive ? "lighter" : "source-over"; };
const selectedEdgeOf = (f) => (f.selectedEdgeId ? f.lights.edgeById.get(f.selectedEdgeId) || null : null);
const screenOf = (f, nid) => { const p = f.layout.positions.get(nid); return toScreen(f.cam, f.view, p.x, p.y); };

/** Screen radius and alpha of one light at its current brightness `d`. */
export function lightLook(node, d, gain, pulse) {
  const style = ROLE_STYLE[node.role];
  const fixed = TONE_FIXED_ALPHA[node.tone];
  if (fixed != null) return { r: style.radius * SHRUNK_RADIUS_SCALE * gain, alpha: fixed };
  const alpha = style.baseAlpha + (1 - style.baseAlpha) * d;
  const r = (style.radius + (style.litRadius - style.radius) * d) * gain;
  return { r, alpha: node.tone === TONES.EVALUATING ? alpha * (1 - pulse) : alpha };
}

/** 1 for the selected node and its neighbours (or the selected edge's two ends, or everything when nothing is selected), else dimmed. */
function emphasisOf(f) {
  const edge = selectedEdgeOf(f);
  if (edge) return (nid) => (nid === edge.src || nid === edge.dst ? 1 : SELECTION.DIM_SCALE);
  const sel = f.selectedId;
  if (!sel || !f.lights.nodes.has(sel)) return () => 1;
  const near = f.lights.adjacency.get(sel) || new Set();
  return (nid) => (nid === sel || near.has(nid) ? 1 : SELECTION.DIM_SCALE);
}

const shown = (f, nid) => { const n = f.lights.nodes.get(nid); return !!n && isNodeShown(n, f.options) && f.layout.positions.has(nid); };

function drawClusters(ctx, f) {
  if (f.cam.scale > f.fitScale * LABELS.CLUSTER_MAX_SCALE_OVER_FIT) return;
  const glow = f.theme.additive ? spriteFor(f.theme.rgb.cluster, false) : null;
  const ring = f.theme.rgb.cluster;
  ctx.font = LABELS.CLUSTER_FONT;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  f.layout.clusters.forEach((c) => {
    const [sx, sy] = toScreen(f.cam, f.view, c.cx, c.cy);
    const r = c.r * f.cam.scale;
    const mass = f.lights.clusterMass.get(c.id) || 0;
    if (glow) {
      blend(ctx, f);
      ctx.globalAlpha = (CLUSTER_GLOW_BASE + CLUSTER_GLOW_MASS * mass) * f.theme.glowAlpha;
      ctx.drawImage(glow, sx - r * 1.4, sy - r * 1.4, r * 2.8, r * 2.8);
    }
    ctx.globalCompositeOperation = "source-over";
    ctx.globalAlpha = 1;
    ctx.beginPath();
    ctx.arc(sx, sy, r, 0, 2 * Math.PI);
    if (f.theme.clusterFillAlpha > 0) {
      ctx.fillStyle = rgbCss(ring, f.theme.clusterFillAlpha + mass * 0.08);
      ctx.fill();
    }
    ctx.strokeStyle = rgbCss(ring, f.theme.clusterRingAlpha + mass * 0.2);
    ctx.lineWidth = 1;
    ctx.stroke();
    if (f.layout.clusters.length > 1) {
      const w = ctx.measureText(c.label).width + 2 * LABELS.CLUSTER_PILL_PAD_PX;
      const y = sy - r - LABELS.OFFSET_PX - LABELS.CLUSTER_PILL_H_PX / 2;
      ctx.fillStyle = rgbCss(f.theme.rgb.bg, CLUSTER_PILL_ALPHA);
      pill(ctx, sx - w / 2, y - LABELS.CLUSTER_PILL_H_PX / 2, w, LABELS.CLUSTER_PILL_H_PX, LABELS.CLUSTER_PILL_H_PX / 2);
      ctx.fillStyle = rgbCss(f.theme.rgb.label, CLUSTER_LABEL_ALPHA);
      ctx.fillText(c.label, sx, y);
    }
  });
  ctx.textBaseline = "alphabetic";
}

const touchesSelection = (f, e) => (f.selectedId && (e.src === f.selectedId || e.dst === f.selectedId)) || e.id === f.selectedEdgeId;

/** Whether an edge is drawn (and hit-testable) under the view options; the selection's edges always are. */
export function edgeShown(f, e) {
  if (!shown(f, e.src) || !shown(f, e.dst)) return false;
  if (touchesSelection(f, e)) return true;
  if (f.options.edgeMode === EDGE_MODES.NONE) return false;
  return e.traversed || f.options.edgeMode === EDGE_MODES.ALL;
}

function edgeAlpha(f, e, emphasis) {
  if (touchesSelection(f, e)) return EDGE.SELECTED_ALPHA;
  const dim = Math.min(emphasis(e.src), emphasis(e.dst));
  let alpha;
  if (!e.traversed) alpha = f.theme.faintEdgeAlpha * dim;
  else if (e.kind === EDGE_KINDS.RULES_OUT) alpha = e.alpha * dim;
  else {
    const na = f.lights.nodes.get(e.src);
    const nb = f.lights.nodes.get(e.dst);
    const target = Math.max(Math.min(na.lit, nb.lit), 1e-3);
    const reached = Math.min(1, Math.min(f.display.get(e.src) ?? 0, f.display.get(e.dst) ?? 0) / target);
    alpha = e.alpha * reached * dim;
  }
  return e.id === f.hoverEdgeId ? Math.max(alpha, EDGE.HOVER_ALPHA) : alpha;
}

function edgeColor(f, e) {
  if (e.kind === EDGE_KINDS.RULES_OUT) return f.theme.rgb.rulesOut;
  if (!e.traversed) return e.id === f.hoverEdgeId || e.id === f.selectedEdgeId ? f.theme.rgb.edgeLit : f.theme.rgb.edge;
  return e.lead ? toneRgb(f.theme, ROLES.CAUSE, TONES.LEADING) : f.theme.rgb.edgeLit;
}

/** Faint edges share one path per kind (one stroke call each); traversed, hovered and selected edges are stroked one by one. */
function drawEdges(ctx, f) {
  const emphasis = emphasisOf(f);
  const faint = { [EDGE_KINDS.SUPPORTS]: new Path2D(), [EDGE_KINDS.RULES_OUT]: new Path2D() };
  const faintDimmed = { [EDGE_KINDS.SUPPORTS]: new Path2D(), [EDGE_KINDS.RULES_OUT]: new Path2D() };
  const strong = [];
  f.lights.edges.forEach((e) => {
    if (!edgeShown(f, e)) return;
    if (e.traversed || touchesSelection(f, e) || e.id === f.hoverEdgeId) { strong.push(e); return; }
    const [x1, y1] = screenOf(f, e.src);
    const [x2, y2] = screenOf(f, e.dst);
    const bucket = Math.min(emphasis(e.src), emphasis(e.dst)) < 1 ? faintDimmed : faint;
    bucket[e.kind].moveTo(x1, y1);
    bucket[e.kind].lineTo(x2, y2);
  });
  ctx.globalCompositeOperation = "source-over";
  ctx.globalAlpha = 1;
  ctx.lineWidth = EDGE.FAINT_WIDTH;
  [[faint, 1], [faintDimmed, SELECTION.DIM_SCALE]].forEach(([paths, dim]) => {
    Object.keys(paths).forEach((kind) => {
      ctx.setLineDash(kind === EDGE_KINDS.RULES_OUT ? EDGE.DASH : []);
      ctx.strokeStyle = rgbCss(kind === EDGE_KINDS.RULES_OUT ? f.theme.rgb.rulesOut : f.theme.rgb.edge, f.theme.faintEdgeAlpha * dim);
      ctx.stroke(paths[kind]);
    });
  });
  blend(ctx, f);
  strong.forEach((e) => {
    const alpha = edgeAlpha(f, e, emphasis);
    if (alpha <= MIN_ALPHA) return;
    const [x1, y1] = screenOf(f, e.src);
    const [x2, y2] = screenOf(f, e.dst);
    ctx.setLineDash(e.kind === EDGE_KINDS.RULES_OUT ? EDGE.DASH : []);
    ctx.lineWidth = Math.max(e.width, EDGE.FAINT_WIDTH) + (e.id === f.selectedEdgeId ? EDGE.SELECTED_EXTRA_WIDTH : 0);
    ctx.strokeStyle = rgbCss(edgeColor(f, e), alpha);
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();
  });
  ctx.setLineDash([]);
  ctx.globalCompositeOperation = "source-over";
  ctx.globalAlpha = 1;
}

/** Night: additive glow sprite + white-hot core. Day: soft fill mixed toward white, crisp 1 px outline, halo only when lit. */
function drawOneNode(ctx, f, node, sx, sy, r, alpha) {
  const rgb = toneRgb(f.theme, node.role, node.tone);
  const reach = r * SPRITE.GLOW_SCALE;
  if (f.theme.additive) {
    const sprite = spriteFor(rgb, true);
    blend(ctx, f);
    if (sprite) {
      ctx.globalAlpha = alpha * f.theme.glowAlpha;
      ctx.drawImage(sprite, sx - reach, sy - reach, reach * 2, reach * 2);
    }
    ctx.globalAlpha = alpha;
    ctx.fillStyle = `rgb(${rgb.map((v) => Math.round(v + (255 - v) * CORE_WHITE_MIX * alpha)).join(",")})`;
    ctx.beginPath();
    ctx.arc(sx, sy, Math.max(0.6, r * CORE_RADIUS_SCALE), 0, 2 * Math.PI);
    ctx.fill();
    return rgb;
  }
  const lit = f.display.get(node.nid) ?? 0;
  if (lit >= DAY_HALO_MIN_LIT) {
    const sprite = spriteFor(rgb, false);
    if (sprite) {
      ctx.globalAlpha = alpha * f.theme.glowAlpha * lit;
      ctx.drawImage(sprite, sx - reach, sy - reach, reach * 2, reach * 2);
    }
  }
  const radius = Math.max(1.2, r * DAY_FILL_RADIUS_SCALE);
  ctx.globalAlpha = alpha;
  ctx.fillStyle = rgbCss(mixRgb(rgb, WHITE, f.theme.softFill * (1 - lit * 0.5)));
  ctx.beginPath();
  ctx.arc(sx, sy, radius, 0, 2 * Math.PI);
  ctx.fill();
  ctx.strokeStyle = rgbCss(rgb);
  ctx.lineWidth = f.theme.outline;
  ctx.stroke();
  return rgb;
}

function drawNodes(ctx, f) {
  const gain = zoomGain(f.cam, f.fitScale);
  const pulse = pulseOf(f.time, f.reducedMotion);
  const emphasis = emphasisOf(f);
  const { width, height } = f.view;
  f.lights.nodes.forEach((node) => {
    const p = f.layout.positions.get(node.nid);
    if (!p || !isNodeShown(node, f.options)) return;
    const [sx, sy] = toScreen(f.cam, f.view, p.x, p.y);
    const look = lightLook(node, f.display.get(node.nid) ?? 0, gain, pulse);
    const alpha = look.alpha * emphasis(node.nid);
    const reach = look.r * SPRITE.GLOW_SCALE;
    if (sx < -reach || sy < -reach || sx > width + reach || sy > height + reach) return;
    const rgb = drawOneNode(ctx, f, node, sx, sy, look.r, alpha);
    if (node.tone === TONES.LEADING) {
      ctx.globalCompositeOperation = "source-over";
      ctx.globalAlpha = HALO_ALPHA * emphasis(node.nid);
      ctx.strokeStyle = rgbCss(rgb);
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.arc(sx, sy, look.r * LEADING_HALO_SCALE, 0, 2 * Math.PI);
      ctx.stroke();
    }
  });
  ctx.globalCompositeOperation = "source-over";
  ctx.globalAlpha = 1;
}

const truncate = (text) => (text.length > LABELS.MAX_CHARS ? `${text.slice(0, LABELS.MAX_CHARS - 1)}…` : text);

/** Labelled nodes: the selection, the hovered node and the selection's neighbours always; the rest by zoom and light. */
function labelCandidates(f) {
  const over = f.cam.scale / f.fitScale;
  const edge = selectedEdgeOf(f);
  const near = edge ? new Set([edge.src, edge.dst]) : f.selectedId ? f.lights.adjacency.get(f.selectedId) || new Set() : new Set();
  const out = [];
  f.lights.nodes.forEach((n) => {
    if (!isNodeShown(n, f.options)) return;
    const d = f.display.get(n.nid) ?? 0;
    if (n.nid === f.selectedId || n.nid === f.hoverId) out.push([n, 10]);
    else if (near.has(n.nid)) out.push([n, 5 + d]);
    else if (!f.options.labels) return;
    else if (n.role === ROLES.CAUSE && over >= LABELS.CAUSE_MIN_SCALE_OVER_FIT && (n.tone === TONES.LEADING || d >= LABELS.CAUSE_MIN_LIT)) out.push([n, d + 1]);
    else if (n.role === ROLES.SYMPTOM && over >= LABELS.SYMPTOM_MIN_SCALE_OVER_FIT && n.tone === TONES.LIT) out.push([n, d]);
  });
  return out.sort((a, b) => b[1] - a[1]).map(([n]) => n);
}

function drawLabels(ctx, f) {
  const placed = [];
  const overlaps = (r) => placed.some((q) => r.x < q.x + q.w && r.x + r.w > q.x && r.y < q.y + q.h && r.y + r.h > q.y);
  const emphasis = emphasisOf(f);
  ctx.font = LABELS.FONT;
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  labelCandidates(f).forEach((n) => {
    if (placed.length >= LABELS.MAX) return;
    if (!f.layout.positions.has(n.nid)) return;
    const [sx, sy] = screenOf(f, n.nid);
    const text = truncate(n.label);
    const w = ctx.measureText(text).width;
    const rect = { x: sx + LABELS.OFFSET_PX - LABELS.PAD_PX, y: sy - LABELS.LINE_PX / 2 - LABELS.PAD_PX, w: w + 2 * LABELS.PAD_PX, h: LABELS.LINE_PX + 2 * LABELS.PAD_PX };
    if (rect.x + rect.w < 0 || rect.x > f.view.width || rect.y > f.view.height || rect.y + rect.h < 0 || overlaps(rect)) return;
    placed.push(rect);
    const alpha = f.theme.labelAlpha * Math.max(emphasis(n.nid), n.nid === f.hoverId ? 1 : 0);
    ctx.fillStyle = rgbCss(f.theme.rgb.bg, LABEL_BG_ALPHA * alpha);
    pill(ctx, rect.x, rect.y, rect.w, rect.h, LABEL_BG_RADIUS_PX);
    ctx.fillStyle = rgbCss(f.theme.rgb.label, alpha);
    ctx.fillText(text, sx + LABELS.OFFSET_PX, sy);
  });
  ctx.textBaseline = "alphabetic";
}

function ring(ctx, f, nid, radius, width, alpha) {
  if (!nid || !f.layout.positions.has(nid)) return;
  const [sx, sy] = screenOf(f, nid);
  ctx.strokeStyle = rgbCss(f.theme.rgb.selection, alpha);
  ctx.lineWidth = width;
  ctx.beginPath();
  ctx.arc(sx, sy, radius, 0, 2 * Math.PI);
  ctx.stroke();
}

export function drawFrame(ctx, f) {
  ctx.globalCompositeOperation = "source-over";
  ctx.globalAlpha = 1;
  ctx.fillStyle = f.theme.bg;
  ctx.fillRect(0, 0, f.view.width, f.view.height);
  drawClusters(ctx, f);
  drawEdges(ctx, f);
  drawNodes(ctx, f);
  drawLabels(ctx, f);
  if (f.hoverId && f.hoverId !== f.selectedId) ring(ctx, f, f.hoverId, HOVER_RADIUS_PX, 1, HOVER_RING_ALPHA);
  ring(ctx, f, f.selectedId, SELECTION.RING_PX, SELECTION.RING_WIDTH, 1);
  const edge = selectedEdgeOf(f);
  if (edge) {
    ring(ctx, f, edge.src, HOVER_RADIUS_PX, SELECTION.RING_WIDTH, 1);
    ring(ctx, f, edge.dst, HOVER_RADIUS_PX, SELECTION.RING_WIDTH, 1);
  }
}

/** The shown node nearest screen (sx, sy) within HOVER_RADIUS_PX, or null. */
export function hitTest(f, sx, sy) {
  let best = null;
  let bestDist = HOVER_RADIUS_PX;
  f.lights.nodes.forEach((n) => {
    const p = f.layout.positions.get(n.nid);
    if (!p || !isNodeShown(n, f.options)) return;
    const [x, y] = toScreen(f.cam, f.view, p.x, p.y);
    const dist = Math.hypot(x - sx, y - sy);
    if (dist < bestDist) { best = n; bestDist = dist; }
  });
  return best;
}

function segmentDistance(px, py, x1, y1, x2, y2) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len2 = dx * dx + dy * dy;
  const t = len2 === 0 ? 0 : Math.max(0, Math.min(1, ((px - x1) * dx + (py - y1) * dy) / len2));
  return Math.hypot(px - (x1 + t * dx), py - (y1 + t * dy));
}

/** The shown edge nearest screen (sx, sy) within EDGE.HIT_PX (independent of its drawn width), or null. Nodes win first. */
export function hitTestEdge(f, sx, sy) {
  let best = null;
  let bestDist = EDGE.HIT_PX;
  f.lights.edges.forEach((e) => {
    if (!edgeShown(f, e)) return;
    const [x1, y1] = screenOf(f, e.src);
    const [x2, y2] = screenOf(f, e.dst);
    if (sx < Math.min(x1, x2) - EDGE.HIT_PX || sx > Math.max(x1, x2) + EDGE.HIT_PX || sy < Math.min(y1, y2) - EDGE.HIT_PX || sy > Math.max(y1, y2) + EDGE.HIT_PX) return;
    const dist = segmentDistance(sx, sy, x1, y1, x2, y2);
    if (dist < bestDist) { best = e; bestDist = dist; }
  });
  return best;
}
